import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useCallback, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryDropdown, type CategoryDropdownItemProps } from '../../../components/categories/CategoryDropdown';
import { setsEqual } from '../../../lib/sets';
import { useSaveWorkCategorySelection, useWorkCategories } from '../api/workCategoriesApi';
import { useDirtyForm } from '../state/useDirtyForm';
import type { WorkCategory, WorkSubCategory } from '../types';
import { SettingsCard } from './SettingsCard';

export type WorkCategoriesCardProps = {
    selectedLeafIds: ReadonlySet<string>;
    readOnly?: boolean;
};

function leafIdsForCategory(category: WorkCategory): string[] {
    if (category.subCategories.length === 0) {
        return [category.id];
    }
    return category.subCategories.flatMap((sub) => sub.items.map((item) => item.id));
}

function leafIdsForSub(sub: WorkSubCategory): string[] {
    return sub.items.map((item) => item.id);
}

function countSelected(ids: readonly string[], selected: ReadonlySet<string>): number {
    return ids.reduce((sum, id) => (selected.has(id) ? sum + 1 : sum), 0);
}

export function WorkCategoriesCard({ selectedLeafIds, readOnly = false }: WorkCategoriesCardProps): ReactElement {
    const { t } = useTranslation();
    const categoriesQuery = useWorkCategories();
    const saveMutation = useSaveWorkCategorySelection();

    const { value, setValue, reset, isDirty } = useDirtyForm<ReadonlySet<string>>(selectedLeafIds, setsEqual);

    const [openCategories, setOpenCategories] = useState<Set<string>>(() => new Set());
    const [openSubCategories, setOpenSubCategories] = useState<Set<string>>(() => new Set());

    const toggleOpen = useCallback((setter: typeof setOpenCategories, id: string) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const toggleManyLeaves = useCallback(
        (ids: readonly string[]) => {
            setValue((prev) => {
                const next = new Set(prev);
                const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
                if (allSelected) {
                    ids.forEach((id) => next.delete(id));
                } else {
                    ids.forEach((id) => next.add(id));
                }
                return next;
            });
        },
        [setValue],
    );

    const toggleLeaf = useCallback(
        (leafId: string) => {
            setValue((prev) => {
                const next = new Set(prev);
                if (next.has(leafId)) {
                    next.delete(leafId);
                } else {
                    next.add(leafId);
                }
                return next;
            });
        },
        [setValue],
    );

    const handleSave = (): void => {
        saveMutation.mutate(value);
    };

    const categories = categoriesQuery.data ?? [];

    return (
        <SettingsCard
            title={t('SETTINGS.COMPANY.WORK_CATEGORIES.TITLE')}
            dirty={isDirty}
            saving={saveMutation.isPending}
            onSave={handleSave}
            onReset={reset}
            readOnly={readOnly}>
            {categoriesQuery.isLoading ? (
                <Stack spacing={1} aria-busy>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={56} />
                    ))}
                </Stack>
            ) : (
                <Stack
                    spacing={0.5}
                    component="ul"
                    sx={{ listStyle: 'none', p: 0, m: 0 }}
                    aria-label={t('SETTINGS.COMPANY.WORK_CATEGORIES.ARIA_LABEL')}>
                    {categories.map((category) => (
                        <li key={category.id}>
                            <CategoryDropdownItem
                                category={category}
                                open={openCategories.has(category.id)}
                                openSubCategories={openSubCategories}
                                selected={value}
                                onToggleCategoryOpen={(id) => toggleOpen(setOpenCategories, id)}
                                onToggleSubOpen={(id) => toggleOpen(setOpenSubCategories, id)}
                                onToggleManyLeaves={toggleManyLeaves}
                                onToggleLeaf={toggleLeaf}
                                disabled={readOnly}
                            />
                        </li>
                    ))}
                </Stack>
            )}
        </SettingsCard>
    );
}

type CategoryDropdownItemInnerProps = {
    category: WorkCategory;
    open: boolean;
    openSubCategories: ReadonlySet<string>;
    selected: ReadonlySet<string>;
    onToggleCategoryOpen: (id: string) => void;
    onToggleSubOpen: (id: string) => void;
    onToggleManyLeaves: (ids: readonly string[]) => void;
    onToggleLeaf: (leafId: string) => void;
    disabled: boolean;
};

function CategoryDropdownItem({
    category,
    open,
    openSubCategories,
    selected,
    onToggleCategoryOpen,
    onToggleSubOpen,
    onToggleManyLeaves,
    onToggleLeaf,
    disabled,
}: CategoryDropdownItemInnerProps): ReactElement {
    const { t } = useTranslation();
    const categoryLeafIds = leafIdsForCategory(category);
    const selectedInCategory = countSelected(categoryLeafIds, selected);
    const fullyChecked = selectedInCategory === categoryLeafIds.length && categoryLeafIds.length > 0;
    const indeterminate = selectedInCategory > 0 && !fullyChecked;

    const items: CategoryDropdownItemProps[] = category.subCategories.map((sub) => {
        const subLeafIds = leafIdsForSub(sub);
        const subSelected = countSelected(subLeafIds, selected);
        const subFullyChecked = subSelected === subLeafIds.length && subLeafIds.length > 0;
        return {
            uuid: sub.id,
            label: t(`WORK_CATEGORY__${sub.id}`),
            count: subSelected,
            items: sub.items.map((item) => ({ uuid: item.id, label: t(`WORK_CATEGORY__${item.id}`) })),
            open: openSubCategories.has(sub.id),
            checked: subFullyChecked,
            indeterminate: subSelected > 0 && !subFullyChecked,
            selected: subLeafIds.filter((id) => selected.has(id)),
            onToggleOpen: () => onToggleSubOpen(sub.id),
            onToggleChecked: () => onToggleManyLeaves(subLeafIds),
            onToggleItem: onToggleLeaf,
        };
    });

    return (
        <CategoryDropdown
            label={t(`WORK_CATEGORY__${category.id}`)}
            count={selectedInCategory}
            items={items}
            open={open}
            checked={fullyChecked}
            indeterminate={indeterminate}
            onToggleOpen={() => onToggleCategoryOpen(category.id)}
            onToggleChecked={() => onToggleManyLeaves(categoryLeafIds)}
            disabled={disabled}
        />
    );
}
